import axiosInstance, { getAPIURL } from "./axiosInstance";

export type TranslationFields = Record<string, string>;

interface TranslationResponse {
  fields?: Record<string, string>;
}

function sanitizeFields(
  fields: Record<string, string | undefined | null>
): TranslationFields {
  const entries = Object.entries(fields).filter(
    ([key, value]) => !!key && typeof value === "string" && value.trim().length > 0
  );
  return Object.fromEntries(entries.map(([key, value]) => [key, value!.trim()]));
}

/** GTX `translate_a/single` mixes string segments and [translated, source, …] tuples. */
function extractGoogleTranslateText(data: unknown): string {
  if (!Array.isArray(data) || !Array.isArray(data[0])) {
    return "";
  }
  const parts: string[] = [];
  for (const segment of data[0]) {
    if (typeof segment === "string") {
      parts.push(segment);
      continue;
    }
    if (!Array.isArray(segment) || segment.length === 0) {
      continue;
    }
    let head: unknown = segment[0];
    while (Array.isArray(head) && head.length > 0) {
      head = head[0];
    }
    if (typeof head === "string") {
      parts.push(head);
    }
  }
  return parts.join("");
}

async function translateViaGoogle(
  fields: TranslationFields,
  sourceLanguage: string,
  targetLanguage: string
): Promise<TranslationFields> {
  const entries = Object.entries(fields);
  const translatedEntries = await Promise.all(
    entries.map(async ([key, value]) => {
      try {
        const sl = encodeURIComponent(sourceLanguage);
        const tl = encodeURIComponent(targetLanguage);
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sl}&tl=${tl}&dt=t&q=${encodeURIComponent(value)}`;
        const res = await fetch(url);
        if (!res.ok) {
          return [key, value] as const;
        }
        const data = await res.json();
        const translatedText = extractGoogleTranslateText(data);
        return [key, translatedText || value] as const;
      } catch {
        return [key, value] as const;
      }
    })
  );
  return Object.fromEntries(translatedEntries);
}

/**
 * Contact / live typing: Google only (`sl=auto`) so a missing `/Translation/batch`
 * API (404) does not block every keystroke behind a failing POST.
 */
export async function translateMessageWithGoogle(
  message: string,
  targetLanguage: string
): Promise<string> {
  const tgt =
    (targetLanguage || "en").split("-")[0]?.toLowerCase() || "en";
  const sanitized = sanitizeFields({ message });
  if (!sanitized.message) {
    return message;
  }
  const input = sanitized.message;
  const norm = (s: string) => s.trim().normalize("NFC");

  const autoResult = await translateViaGoogle({ message: input }, "auto", tgt);
  let out = autoResult.message ?? input;

  /**
   * After the first translation, the textarea is mostly in `tgt`. `sl=auto` then
   * detects `tgt` and `tl=tgt` becomes translate-French-to-French → no change,
   * so appended English never updates. Retry once as English source so new
   * Latin text is still translated into `tgt`.
   */
  if (tgt !== "en" && norm(out) === norm(input)) {
    const enResult = await translateViaGoogle({ message: input }, "en", tgt);
    const outEn = enResult.message ?? out;
    if (norm(outEn) !== norm(input)) {
      return outEn;
    }
  }

  if (
    tgt === "en" &&
    norm(out) === norm(input) &&
    /[\u0900-\u097F]/.test(input)
  ) {
    const hiResult = await translateViaGoogle({ message: input }, "hi", "en");
    const outHi = hiResult.message ?? out;
    if (norm(outHi) !== norm(input)) {
      return outHi;
    }
  }

  return out;
}

/**
 * Translates a batch of string fields via `/Translation/batch`, then Google
 * Translate (same behaviour as `useTranslateDynamicFields`).
 */
export async function translateBatchFields(
  fields: Record<string, string | undefined | null>,
  sourceLanguage: string,
  targetLanguage: string
): Promise<TranslationFields> {
  const rawSrc = (sourceLanguage || "en").split("-")[0]?.toLowerCase() || "en";
  const tgt = (targetLanguage || "en").split("-")[0]?.toLowerCase() || "en";
  const src = rawSrc === "auto" ? "auto" : rawSrc;
  const sanitizedFields = sanitizeFields(fields);
  if (Object.keys(sanitizedFields).length === 0) {
    return {};
  }
  if (src !== "auto" && src === tgt) {
    return { ...sanitizedFields };
  }

  try {
    const response = await axiosInstance.post<TranslationResponse>(
      getAPIURL("/Translation/batch"),
      {
        sourceLanguage: src,
        targetLanguage: tgt,
        fields: sanitizedFields,
      }
    );
    const backendFields = response.data?.fields ?? {};
    const hasBackendValues = Object.keys(backendFields).length > 0;
    const hasActualTranslation = Object.entries(sanitizedFields).some(
      ([key, original]) => {
        const translated = backendFields[key];
        return (
          typeof translated === "string" &&
          translated.trim().length > 0 &&
          translated.trim().toLowerCase() !== original.trim().toLowerCase()
        );
      }
    );
    if (hasBackendValues && hasActualTranslation) {
      return backendFields;
    }
  } catch {
    // fall through to Google
  }

  return translateViaGoogle(sanitizedFields, src, tgt);
}
